// LessonOverlay.jsx

import React, {useState, useRef} from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Animated,
} from 'react-native-web';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {disableWebboard, enableWebboard} from 'store/media';
import {useDispatch, useSelector} from 'react-redux';
import World from '../../../../components/World';
import {
  setCourseData,
  setCurrentLessonProgress,
} from '../../../../store/course';
import useLessonProgressUpdater from '../../../../hooks/useCourseProgressUpdater';
import {useEffect} from 'react';

const LessonOverlay = () => {
  const {updateLessonProgressStates} = useLessonProgressUpdater();

  const {courseData, loading, error} = useSelector(state => state.course);

  const currentLessonProgress = useSelector(
    state => state.course.currentLessonProgress,
  );

  const ready_to_move = useSelector(
    state => state.meeting.meeting.ready_to_move,
  );

  const currentUnitIndex = courseData && courseData.currentUnitIndex;

  const totalUnits =
    courseData && courseData.units ? courseData.units.length : 0;
  const unit =
    courseData && courseData.units && courseData.units[currentUnitIndex];
  const currentUnitNumber = currentUnitIndex + 1;
  const lessons = unit ? unit.lessons : [];

  const [expandedLessonId, setExpandedLessonId] = useState(null);
  const [overflowLessonId, setOverflowLessonId] = useState(null);
  const [hoveredLessonId, setHoveredLessonId] = useState(null);
  const [hoveredRowId, setHoveredRowId] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({top: 0, left: 0});
  const [hoveredButton, setHoveredButton] = useState({
    lessonId: null,
    button: null,
  });

  // Add this line:
const [expandedOverlay, setExpandedOverlay] = useState(true);

const toggleAll = () => {
  setExpandedOverlay(!expandedOverlay);
};

  const oneToOneMeetingStatus = useSelector(
    state => state.communication.speechToTextService.oneToOneMeetingStatus,
  );

// after – only run when lessons array changes
useEffect(() => {
  if (lessons.length > 0 && expandedLessonId === null) {
    setExpandedLessonId(lessons[0].lessonId);
    setOverflowLessonId(lessons[0].lessonId);
  }
// prettier-ignore
}, [lessons]);  // <-- expandedLessonId removed so collapsing doesn't re-trigger it


  const [isHovered, setIsHovered] = useState(true);

  const dispatch = useDispatch();
  const userId = useSelector(state => state.app.courseTokenValues.userId);
  const token = useSelector(state => state.meeting.properties.token);

  const toggleLesson = lessonId => {
    if (expandedLessonId === lessonId) {
      // collapsing
      setExpandedLessonId(null);
      setTimeout(() => {
        setOverflowLessonId(null);
      }, 400);
     
    } else {
      // expanding
      setExpandedLessonId(lessonId);
      // allow overflow after animation (~0.1s)
      setTimeout(() => {
        setOverflowLessonId(lessonId);
      }, 100);
    }
  };

  // New methods added
  const handleStartLesson = lessonId => {
    console.log('Start Learning for LessonId:', lessonId);

    const currentLesson = lessons.find(lesson => lesson.lessonId === lessonId);
    const lessonProgress = currentLesson.progress;
    var updatedCourseData = {
      ...courseData,
      currentLessonId: lessonId,
      currentLessonTotalSlides: currentLesson.totalSlides,
    };

    const videoIndexToPlay = Math.max(
      0,
      Math.round((lessonProgress / 100) * currentLesson.totalSlides) - 1,
    );

    World.runCommand(`start_lesson ${lessonId} ${videoIndexToPlay}`);
    dispatch(setCourseData(updatedCourseData));

    updateCurrentLessonProgressData({
      lessonId: lessonId,
      lessonProgress: lessonProgress,
      totalSlides: currentLesson.totalSlides,
      lessonMasteryScore: currentLesson.masteryScore,
      reset: lessonId !== currentLessonProgress.currentLessonId, // We only want to reset if we are starting a different lesson id
    });
  };

  const handleStartAssessment = lessonId => {
    World.runCommand(`pause_lesson true`);
    const currentLesson = lessons.find(lesson => lesson.lessonId === lessonId);
    const lessonProgress = currentLesson.progress;
    console.log('Start Assessment for LessonId:', lessonId);
    updateCurrentLessonProgressData({
      lessonId: lessonId,
      lessonProgress: lessonProgress,
      totalSlides: currentLesson.totalSlides,
      lessonMasteryScore: currentLesson.masteryScore,
    });
    dispatch(
      enableWebboard(
        `https://assessment.quiklearn.ai/?unit_id=${unit.id}&lesson_id=${lessonId}&user_id=${userId}&token=${token}&currentAssessmentScore=${currentLesson.masteryScore}`,
      ),
    );
  };

  const updateCurrentLessonProgressData = ({
    lessonId,
    lessonProgress,
    totalSlides,
    lessonMasteryScore,
    reset,
  }) => {
    if (reset) {
      dispatch(
        setCurrentLessonProgress({
          currentLessonId: lessonId,
          currentLessonProgressPercentage: lessonProgress,
          currentLessonTotalSlides: totalSlides,
          currentLessonSlidesWatched: 0,
          currentLessonStartTimeStamp: Date.now(),
          currentLessonMasteryScore: lessonMasteryScore,
        }),
      );
    } else {
      dispatch(
        setCurrentLessonProgress({
          currentLessonId: lessonId,
          currentLessonProgressPercentage: lessonProgress,
          currentLessonTotalSlides: totalSlides,
          currentLessonMasteryScore: lessonMasteryScore,
        }),
      );
    }
  };

  useEffect(() => {
    updateLessonProgressStates({
      lessonId: currentLessonProgress.currentLessonId,
      newProgress: currentLessonProgress.currentLessonProgressPercentage,
      newLessonMasteryScore: currentLessonProgress.currentLessonMasteryScore,
    });
  }, [currentLessonProgress]);

  // Use the assessmentMasteryPercentage as the mastery indicator.
  const calculateMastery = lesson => {
    return Math.min(
      Math.round((lesson.masteryScore / unit.lessonMasteryScore) * 100),
      100,
    );
  };

  const getStatusBadge = (masteryProgress, lessonProgress) => {
    if (masteryProgress >= 95) {
      return (
        <View style={[styles.badge, {backgroundColor: '#f59e0b'}]}>
          <Text style={styles.badgeText}>Expert {masteryProgress}%</Text>
        </View>
      );
    } else if (masteryProgress >= 90 && masteryProgress < 95) {
      return (
        <View style={[styles.badge, {backgroundColor: '#065f46'}]}>
          <Text style={styles.badgeText}>Mastered {masteryProgress}%</Text>
        </View>
      );
    } else if (masteryProgress === 0 && lessonProgress === 0) {
      return (
        <View style={[styles.badge, {backgroundColor: '#fca5a5'}]}>
          <Text style={styles.badgeText}>Not started</Text>
        </View>
      );
    }
    return <Text style={styles.statusDefault}>{masteryProgress}%</Text>;
  };

  const getContentTypeIcon = contentType => {
    switch (contentType) {
      case 'video':
        return <Icon name="play-circle-outline" size={14} color="#BDDE36" />;
      case 'text':
        return <Icon name="file-outline" size={14} color="#BDDE36" />;
      case 'interactive':
        return <Icon name="book-outline" size={14} color="#BDDE36" />;
      default:
        return <Icon name="play-circle-outline" size={14} color="#BDDE36" />;
    }
  };

  // Use unit progress from the unit data
  const unitProgress = unit ? unit.unitMasteryPercentage : 0;

  /* ----------------------------------
   * Pulsing animation for action buttons
   * ---------------------------------- */
  const buttonAnim = useRef(new Animated.Value(0)).current;
  const loopRef = useRef(null);

  useEffect(() => {
    // Whenever tutor mode ends (overlay visible) start pulse; stop otherwise.
    if (!oneToOneMeetingStatus) {
      // Restart animation
      buttonAnim.setValue(0);
      loopRef.current = Animated.loop(
        Animated.sequence([
          Animated.timing(buttonAnim, {
            toValue: 1,
            duration: 500,
            useNativeDriver: true,
          }),
          Animated.timing(buttonAnim, {
            toValue: 0,
            duration: 500,
            useNativeDriver: true,
          }),
        ]),
      );
      loopRef.current.start();
    } else if (loopRef.current) {
      loopRef.current.stop();
    }

    return () => {
      if (loopRef.current) loopRef.current.stop();
    };
  }, [oneToOneMeetingStatus, buttonAnim]);

  const pulseScale = buttonAnim.interpolate({ inputRange: [0, 1], outputRange: [1, 1.1] });

  if (!ready_to_move || oneToOneMeetingStatus) return;

  if (loading) {
    return (
      <View
        style={[styles.container, {opacity: isHovered ? 1 : 0.4}]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <Text style={{color: '#FFF', padding: 12}}>Loading...</Text>
      </View>
    );
  }

  if (error || !unit) {
    return (
      <View
        style={[styles.container, {opacity: isHovered ? 1 : 0.4}]}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        <Text style={{color: '#FFF', padding: 12}}>
          {error || 'No unit data available'}
        </Text>
      </View>
    );
  }

  return (
    <View
      style={[styles.container, {opacity: isHovered ? 1 : 0.4}]}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <View style={styles.overlay}>
        {/* Header */}
        <TouchableOpacity
          style={styles.header}
          activeOpacity={0.7}
          onPress={toggleAll}
        >
          <View style={styles.headerLeft}>
            <Text style={styles.headerTitle}>Unit Progress</Text>
          </View>
          <View style={styles.headerRight}>

            <Text style={styles.unitLabel} numberOfLines={2}>
              Unit {currentUnitNumber}: {unit.title}
            </Text>
            <Text style={styles.unitNumber}>
              ({currentUnitNumber}/{totalUnits})
            </Text>

            <View style={styles.courseProgressContainer}>
              <Text style={styles.progressLabel}>Unit Mastery</Text>
              <Text style={styles.progressValue}>{unitProgress}%</Text>
            </View>
            <View style={styles.progressBarOuter}>
              <View
                style={[styles.progressBarInner, { width: `${unitProgress}%` }]}
              />
            </View>
          </View>
          <Icon
            name={expandedOverlay ? 'chevron-up' : 'chevron-down'}
            size={16}
            color="#BDDE36"
            style={{ marginLeft: 6 }}
          />
        </TouchableOpacity>

        <ScrollView style={[
          styles.lessonsContainer,
          expandedOverlay
            ? styles.lessonsContainerExpanded
            : styles.lessonsContainerCollapsed,
        ]}>
          {lessons.map(lesson => {
            // Mapping store lesson fields to component expected names:
            // lessonId, lessonName, lessonDescription, contentType, duration, slidesWatched,
            // totalSlides, and assessmentMasteryPercentage.
            const lessonId = lesson.lessonId;
            const progress = calculateMastery(lesson);
            const learningProgress = lesson.progress;
            const isExpanded = lessonId === expandedLessonId;
            return (
              <View key={lessonId} style={styles.lessonWrapper}>
                {/* LESSON HEADER */}
                <TouchableOpacity
                  activeOpacity={1}
                  style={[
                    styles.lessonHeader,
                    hoveredRowId === lessonId && styles.lessonHeaderHover,
                  ]}
                  onMouseEnter={() => setHoveredRowId(lessonId)}
                  onMouseLeave={() => setHoveredRowId(null)}
                  onPress={() => toggleLesson(lessonId)}>
                  <Text style={styles.lessonTitle}>{lesson.lessonName}</Text>
                  {/* Info Icon + Tooltip */}
                  <View
                    onMouseEnter={e => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const tooltipWidth = 220;
                      const tooltipLeft =
                        rect.left + rect.width / 2 - tooltipWidth / 2;
                      setTooltipPosition({
                        top: rect.bottom + 5,
                        left: tooltipLeft,
                      });
                      setHoveredLessonId(lessonId);
                    }}
                    onMouseLeave={() => setHoveredLessonId(null)}
                    style={{position: 'relative', marginHorizontal: 2}}>
                    <Icon
                      name="information-outline"
                      size={14}
                      color="#BDDE36"
                    />
                  </View>
                  {hoveredLessonId === lessonId &&
                    ReactDOM.createPortal(
                      <View
                        style={[
                          styles.tooltip,
                          {
                            top: tooltipPosition.top,
                            left: tooltipPosition.left,
                          },
                        ]}>
                        <Text style={styles.tooltipText}>
                          {lesson.lessonDescription}
                        </Text>
                        {/* Content Type Row */}
                        <View style={styles.tooltipRow}>
                          <Text style={styles.tooltipLabel}>Content Type:</Text>
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}>
                            {getContentTypeIcon(lesson.content)}
                            <Text
                              style={[styles.tooltipValue, {marginLeft: 4}]}>
                              {lesson.content}
                            </Text>
                          </View>
                        </View>
                        {/* Assessment Row */}
                        <View style={styles.tooltipRow}>
                          <Text style={styles.tooltipLabel}>Assessment:</Text>
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}>
                            <Icon
                              name="clipboard-check"
                              size={14}
                              color="#BDDE36"
                            />
                            <Text
                              style={[styles.tooltipValue, {marginLeft: 4}]}>
                              {progress}% mastery
                            </Text>
                          </View>
                        </View>
                        {/* Duration Row */}
                        <View style={styles.tooltipRow}>
                          <Text style={styles.tooltipLabel}>Duration:</Text>
                          <View
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                            }}>
                            <Icon
                              name="clock-outline"
                              size={14}
                              color="#BDDE36"
                            />
                            <Text
                              style={[styles.tooltipValue, {marginLeft: 4}]}>
                              {lesson.duration} minutes
                            </Text>
                          </View>
                        </View>
                      </View>,
                      document.body,
                    )}
                  {getStatusBadge(progress, learningProgress)}
                  <Icon
                    name={isExpanded ? 'chevron-up' : 'chevron-down'}
                    size={16}
                    color="#BDDE36"
                    style={{marginLeft: 8}}
                  />
                </TouchableOpacity>

                {/* EXPANDED CONTENT */}
                <View
                  style={[
                    styles.expandedContent,
                    isExpanded ? styles.expanded : styles.collapsed,
                    overflowLessonId === lessonId && styles.overflowVisible,
                  ]}>
                  <View style={styles.progressRow}>
                    <Text style={styles.progressRowLabel}>
                      Assessment Mastery
                    </Text>
                    <Text style={styles.progressRowValue}>{progress}%</Text>
                  </View>
                  <View style={styles.smallBarOuter}>
                    <View
                      style={[styles.smallBarInner, {width: `${progress}%`}]}
                    />
                  </View>

                  <View style={styles.progressRow}>
                    <Text style={styles.progressRowLabel}>
                      Learning Content
                    </Text>
                    <Text style={styles.progressRowValue}>
                      {learningProgress}%
                    </Text>
                  </View>
                  <View style={styles.smallBarOuter}>
                    <View
                      style={[
                        styles.smallBarInner,
                        {width: `${learningProgress}%`},
                      ]}
                    />
                  </View>

                  {/* BUTTONS */}
                  <View style={styles.buttonRow}>
                    {/* Start Learning */}
                    <Animated.View
                      style={{
                        transform: [
                          { scale: pulseScale },
                          {
                            scale:
                              hoveredButton.lessonId === lessonId &&
                              hoveredButton.button === 'startLearning'
                                ? 1.2
                                : 1,
                          },
                        ],
                        overflow: 'visible',
                      }}
                    >
                      <TouchableOpacity
                        activeOpacity={1}
                        onPress={() => handleStartLesson(lessonId)}
                        onMouseEnter={() =>
                          setHoveredButton({lessonId, button: 'startLearning'})
                        }
                        onMouseLeave={() =>
                          setHoveredButton({lessonId: null, button: null})
                        }
                        style={[
                          styles.actionButton,
                          hoveredButton.lessonId === lessonId &&
                          hoveredButton.button === 'startLearning'
                            ? styles.actionButtonHover
                            : {},
                        ]}>
                        <Icon
                          name="book-outline"
                          size={14}
                          style={[
                            styles.actionButtonIcon,
                            hoveredButton.lessonId === lessonId &&
                            hoveredButton.button === 'startLearning'
                              ? styles.actionButtonIconHover
                              : {},
                          ]}
                        />
                        <Text
                          style={[
                            styles.buttonText,
                            hoveredButton.lessonId === lessonId &&
                            hoveredButton.button === 'startLearning'
                              ? styles.buttonTextHover
                              : {},
                          ]}>
                          Start Learning
                        </Text>
                      </TouchableOpacity>
                    </Animated.View>

                    {/* Start Assessment */}
                    <Animated.View
                      style={{
                        transform: [
                          { scale: pulseScale },
                          {
                            scale:
                              hoveredButton.lessonId === lessonId &&
                              hoveredButton.button === 'startAssessment'
                                ? 1.2
                                : 1,
                          },
                        ],
                        overflow: 'visible',
                      }}
                    >
                      <TouchableOpacity
                        activeOpacity={1}
                        onPress={() => handleStartAssessment(lessonId)}
                        onMouseEnter={() =>
                          setHoveredButton({lessonId, button: 'startAssessment'})
                        }
                        onMouseLeave={() =>
                          setHoveredButton({lessonId: null, button: null})
                        }
                        style={[
                          styles.actionButton,
                          hoveredButton.lessonId === lessonId &&
                          hoveredButton.button === 'startAssessment'
                            ? styles.actionButtonHover
                            : {},
                        ]}>
                        <Icon
                          name="clipboard-check-outline"
                          size={14}
                          style={[
                            styles.actionButtonIcon,
                            hoveredButton.lessonId === lessonId &&
                            hoveredButton.button === 'startAssessment'
                              ? styles.actionButtonIconHover
                              : {},
                          ]}
                        />
                        <Text
                          style={[
                            styles.buttonText,
                            hoveredButton.lessonId === lessonId &&
                            hoveredButton.button === 'startAssessment'
                              ? styles.buttonTextHover
                              : {},
                          ]}>
                          Start Assessment
                        </Text>
                      </TouchableOpacity>
                    </Animated.View>
                  </View>
                </View>
              </View>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );
};

export default LessonOverlay;

const styles = StyleSheet.create({
  /* Container & Overlay */
  container: {
    position: 'absolute',
    top: 16,
    right: 20,
    zIndex: 50,
    minWidth: 350,
    marginRight: 50,
    borderRadius: 8,
    shadowColor: 'rgba(197, 232, 55,1)',
    shadowOffset: {width: 0, height: 0},
    shadowOpacity: 0.35,
    shadowRadius: 15,
    elevation: 20,
  },

  overlay: {
    backgroundColor: 'rgba(0,0,0,0.8)',
    borderColor: 'rgba(189, 222, 54,0.3)',
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
  },
  /* Header */
  header: {
    backgroundColor: 'rgba(189, 222, 54,0.1)',
    borderBottomColor: 'rgba(189, 222, 54,0.2)',
    borderBottomWidth: 1,
    padding: 12,
    flexDirection: 'row',
  },
  headerLeft: {
    justifyContent: 'center',
    flex: 0.5,
  },
  headerRight: {
    flex: 0.5,
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontWeight: 'bold',
    color: '#BDDE36',
    fontSize: 14,
    textAlignVertical: 'center',
  },
  unitHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    width: '100%',
  },
  // Increase the width of the unit title and prepend unit number in title
  unitLabel: {
    color: '#BDDE36',
    fontSize: 14,
    fontWeight: '500',
    marginRight: 6,
    textAlign: 'right',
  },
  unitNumber: {
    color: '#a1a1a1',
    fontSize: 12,
  },
  courseProgressContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
    width: 130,
  },
  progressLabel: {
    fontSize: 9,
    color: '#a1a1a1',
    width: '100%',
  },
  progressValue: {
    fontSize: 9,
    color: '#BDDE36',
    marginRight: 6,
  },
  progressBarOuter: {
    width: 130,
    height: 4,
    backgroundColor: 'rgba(0,0,0,0.3)',
    marginTop: 3,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressBarInner: {
    height: '100%',
    backgroundColor: '#BDDE36',
    width: '100%',
  },
  /* Lesson Rows */
 lessonsContainer: {
   overflow: 'hidden',
   transition: 'max-height 0.3s linear',
 },
 lessonsContainerExpanded: {
   maxHeight: 360,
 },
 lessonsContainerCollapsed: {
   maxHeight: 0,
 },
  lessonWrapper: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(189, 222, 54,0.2)',
  },
  lessonHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    transition: 'background-color 0.3s ease',
  },
  lessonHeaderHover: {
    backgroundColor: 'rgba(189, 222, 54,0.1)',
  },
  lessonTitle: {
    fontSize: 12,
    color: '#FFF',
    flexShrink: 1,
    marginRight: 6,
  },
  /* Expand/Collapse Animation */
  expandedContent: {
    width: '90%',
    alignSelf: 'center',
    overflow: 'hidden',
    transition: 'max-height 0.5s linear',
  },
  expanded: {
    maxHeight: 500,
  },
  overflowVisible: {
    overflow: 'visible',
  },
  collapsed: {
    maxHeight: 0,
  },
  /* Progress Rows */
  progressRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  progressRowLabel: {
    fontSize: 12,
    color: '#a1a1a1',
  },
  progressRowValue: {
    fontSize: 12,
    color: '#a1a1a1',
  },
  smallBarOuter: {
    height: 5,
    backgroundColor: '#333',
    borderRadius: 4,
    overflow: 'hidden',
    marginTop: 4,
  },
  smallBarInner: {
    height: '100%',
    backgroundColor: '#BDDE36',
  },
  /* Buttons */
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    marginBottom: 20,
  },
  actionButton: {
    backgroundColor: '#FFD700',
    borderRadius: 8,
    borderColor: 'rgba(189, 222, 54,0.2)',
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: 120,
    justifyContent: 'center',
    marginRight: 6,
    transition: 'background-color 0.3s ease',
  },

  actionButtonIcon: {
    color: '#000',
    marginRight: 4,
    transition: 'color 0.3s ease',
  },
  actionButtonIconHover: {
    color: '#000',
  },
  buttonText: {
    fontSize: 13,
    transition: 'color 0.3s ease',
    borderColor: '#CAFF33',
    color: '#000',
  },
  buttonTextHover: {
    color: '#000',
  },
  singleButtonRow: {
    marginTop: 10,
    marginBottom: 10,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  actionButtonGhost: {
    backgroundColor: 'transparent',
    paddingHorizontal: 10,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'center',
  },
  actionButtonGhostHover: {
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 8,
  },
  ghostButtonIcon: {
    color: '#a1a1a1',
    marginRight: 4,
    transition: 'color 0.3s ease',
  },
  ghostButtonIconHover: {
    color: '#A8C434',
  },
  ghostButtonText: {
    color: '#a1a1a1',
    fontSize: 13,
    marginLeft: 0,
    transition: 'color 0.3s ease',
  },
  ghostButtonTextHover: {
    color: '#A8C434',
  },
  /* Badge / Status */
  badge: {
    borderRadius: 50,
    paddingHorizontal: 8,
    paddingVertical: 3,
    marginLeft: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
  },
  badgeText: {
    color: '#FFF',
    fontSize: 12,
  },
  statusDefault: {
    fontSize: 12,
    color: '#BDDE36',
    marginLeft: 'auto',
  },
  /* Tooltip */
  tooltip: {
    position: 'fixed',
    backgroundColor: 'rgba(0,0,0,0.9)',
    borderRadius: 8,
    padding: 8,
    width: 220,
    zIndex: 9999,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.3,
    shadowRadius: 6,
  },
  tooltipText: {
    color: '#fff',
    fontSize: 12,
    marginBottom: 6,
    lineHeight: 16,
    fontWeight: '500',
  },
  tooltipRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  tooltipLabel: {
    color: '#a1a1a1',
    fontSize: 11,
    fontWeight: '500',
  },
  tooltipValue: {
    color: '#fff',
    fontSize: 11,
    textAlign: 'right',
  },
});
